import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SidebarProvider } from "@/context/SidebarContext";
import { TradingModeProvider } from "@/context/TradingModeContext";
import { PushNotificationProvider } from "@/context/PushNotificationContext";
import "@/i18n";
import GlobalLayout from "@/layouts/GlobalLayout";
import GlobalVoiceAssistant from "@/components/GlobalVoiceAssistant";

// Critical path (eager) - pages that load on first paint
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";

// Lazy-loaded pages - code-split for performance
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const BotConfig = lazy(() => import("@/pages/BotConfig"));
const CopyTrading = lazy(() => import("@/pages/CopyTrading"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const Settings = lazy(() => import("@/pages/Settings"));
const Trading = lazy(() => import("@/pages/Trading"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const Education = lazy(() => import("@/pages/Education"));
const BeginnersGuide = lazy(() => import("@/pages/BeginnersGuide"));
const Terms = lazy(() => import("@/pages/Terms"));
const PaymentTerms = lazy(() => import("@/pages/PaymentTerms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const RiskDisclosure = lazy(() => import("@/pages/RiskDisclosure"));
const Journal = lazy(() => import("@/pages/Journal"));
const Performance = lazy(() => import("@/pages/Performance"));
const AISignals = lazy(() => import("@/pages/AISignals"));
const EconomicCalendar = lazy(() => import("@/pages/EconomicCalendar"));
const StrategyMarketplace = lazy(() => import("@/pages/StrategyMarketplace"));
const BotPerformanceDashboard = lazy(() => import("@/pages/BotPerformanceDashboard"));
const BotDetailPage = lazy(() => import("@/pages/BotDetailPage"));
const GridBots = lazy(() => import("@/pages/GridBots"));
const DCABots = lazy(() => import("@/pages/DCABots"));
const SmartTrades = lazy(() => import("@/pages/SmartTrades"));
const Backtesting = lazy(() => import("@/pages/Backtesting"));
const Training = lazy(() => import("@/pages/Training"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const Portfolio = lazy(() => import("@/pages/Portfolio"));
const PaperTrading = lazy(() => import("@/pages/PaperTrading"));
const AIStrategyBuilder = lazy(() => import("@/pages/AIStrategyBuilder"));
const AdvancedAnalytics = lazy(() => import("@/pages/AdvancedAnalytics"));
const Arbitrage = lazy(() => import("@/pages/Arbitrage"));
const AlertChannels = lazy(() => import("@/pages/AlertChannels"));
const SMSAlerts = lazy(() => import("@/pages/SMSAlerts"));
const PowerBot = lazy(() => import("@/pages/PowerBot"));
const StockDashboard = lazy(() => import("@/pages/StockDashboard"));
const OptionsTrading = lazy(() => import("@/pages/OptionsTrading"));
const BrokerGuide = lazy(() => import("@/pages/BrokerGuide"));
const Charts = lazy(() => import("@/pages/Charts"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const MultiAccount = lazy(() => import("@/pages/MultiAccount"));
const TaxReporting = lazy(() => import("@/pages/TaxReporting"));
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const AffiliatePage = lazy(() => import("@/pages/AffiliatePage"));
const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const LegalHub = lazy(() => import("@/pages/LegalHub"));
const LegalDocuments = lazy(() => import("@/pages/LegalDocuments"));
const AgentAgreement = lazy(() => import("@/pages/AgentAgreement"));
const WhiteLabelAgreement = lazy(() => import("@/pages/WhiteLabelAgreement"));
const DeveloperMSA = lazy(() => import("@/pages/DeveloperMSA"));
const GovernanceCharter = lazy(() => import("@/pages/GovernanceCharter"));
const PlatformPage = lazy(() => import("@/pages/PlatformPage"));
const PublicPricingPage = lazy(() => import("@/pages/PublicPricingPage"));
const WhiteLabelPage = lazy(() => import("@/pages/WhiteLabelPage"));
const WhiteLabelDemo = lazy(() => import("@/pages/WhiteLabelDemo"));
const PowerBotSuite = lazy(() => import("@/pages/PowerBotSuite"));
const ForexTrading = lazy(() => import("@/pages/ForexTrading"));
const PlatformDemo = lazy(() => import("@/pages/PlatformDemo"));
const AdvancedAIEngine = lazy(() => import("@/pages/AdvancedAIEngine"));
const AccuracyDashboard = lazy(() => import("@/pages/AccuracyDashboard"));
const DeploymentChecklist = lazy(() => import("@/pages/DeploymentChecklist"));
const TaskMonitor = lazy(() => import("@/pages/TaskMonitor"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
const TradingViewAlertsPage = lazy(() => import("@/pages/TradingViewAlertsPage"));
const PineScriptPage = lazy(() => import("@/pages/PineScriptPage"));
const LiveTrading = lazy(() => import("@/pages/LiveTrading"));
const NotificationBots = lazy(() => import("@/pages/NotificationBots"));
const PortfolioRebalancing = lazy(() => import("@/pages/PortfolioRebalancing"));
const MCASPage = lazy(() => import("@/pages/MCASPage"));
const RLOptimizer = lazy(() => import("@/pages/RLOptimizer"));
const OrderBookWhalesDashboard = lazy(() => import("@/pages/OrderBookWhalesDashboard"));
const ExchangeKeysSettings = lazy(() => import("@/pages/ExchangeKeysSettings"));
const CopyTradingPage = lazy(() => import("@/pages/CopyTradingPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const MarketDataHealth = lazy(() => import("@/pages/MarketDataHealth"));
const StrategyPerformancePage = lazy(() => import("@/pages/StrategyPerformancePage"));
const SignalBridgeDashboard = lazy(() => import("@/pages/SignalBridgeDashboard"));
const TradeActivityFeed = lazy(() => import("@/pages/TradeActivityFeed"));
const BacktestEngine = lazy(() => import("@/pages/BacktestEngine"));
const StrategyOptimizer = lazy(() => import("@/pages/StrategyOptimizer"));
const SwingActivityLog = lazy(() => import("@/pages/SwingActivityLog"));
const TradeQualityDashboard = lazy(() => import("@/pages/TradeQualityDashboard"));
const OpenPositionsPage = lazy(() => import("@/pages/OpenPositionsPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (e) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, referralCode = null, selectedPlan = 'pro') => {
    const payload = { email, password, name, selected_plan: selectedPlan };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, register, logout, checkAuth };
};

// Protected route component with global layout
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-sky-600 font-mono">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return (
    <TradingModeProvider>
      <GlobalLayout user={user}>
        {children}
      </GlobalLayout>
    </TradingModeProvider>
  );
};

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      <span className="text-sm text-slate-500 font-mono">Loading module...</span>
    </div>
  </div>
);

// App Router with auth check
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();
  
  // Handle OAuth callback - check URL fragment for session_id
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onAuthSuccess={(userData) => auth.checkAuth()} />;
  }
  
  return (
    <>
      {/* Global Voice Assistant - available on all authenticated pages */}
      <GlobalVoiceAssistant user={auth.user} isAuthenticated={!!auth.user} />
      
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login auth={auth} />} />
      <Route path="/register" element={<Register auth={auth} />} />
      <Route path="/pricing" element={<Pricing auth={auth} />} />
      <Route path="/plans" element={<Pricing auth={auth} />} />
      <Route path="/auth/callback" element={<AuthCallback onAuthSuccess={() => auth.checkAuth()} />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Dashboard auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <BotConfig auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/copy-trading"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <CopyTradingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Settings auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Trading auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Analytics auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <AdminPanel auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Journal auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <BotPerformanceDashboard auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-signals"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <AISignals auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tradingview-alerts"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <TradingViewAlertsPage auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pine-script"
        element={<PineScriptPage />}
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <EconomicCalendar auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <StrategyMarketplace auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bot-performance"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <BotPerformanceDashboard auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/:botId/performance"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <BotDetailPage auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grid-bots"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <GridBots auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dca-bots"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <DCABots auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smart-trades"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <SmartTrades auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backtesting"
        element={
          <ProtectedRoute user={auth.user} loading={auth.loading}>
            <Backtesting auth={auth} />
          </ProtectedRoute>
        }
      />
      <Route path="/education" element={<Education />} />
      <Route path="/beginners-guide" element={<BeginnersGuide />} />
      <Route path="/system-status" element={<StatusPage />} />
      <Route path="/system-status/" element={<StatusPage />} />
      <Route path="/market-health" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <MarketDataHealth />
        </ProtectedRoute>
      } />
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/agent-dashboard" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <AgentDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/training" element={<Training />} />
      <Route path="/alerts" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Alerts auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/portfolio" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Portfolio auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/paper-trading" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <PaperTrading auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/ai-strategy" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <AIStrategyBuilder auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/advanced-analytics" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <AdvancedAnalytics auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/arbitrage" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Arbitrage auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/alert-channels" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <AlertChannels auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/sms-alerts" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SMSAlerts auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/live-trading" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <LiveTrading auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/notification-bots" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <NotificationBots auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/rebalancing" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <PortfolioRebalancing auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/market-intelligence" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <MCASPage user={auth.user} />
        </ProtectedRoute>
      } />
      <Route path="/rl-optimizer" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <RLOptimizer user={auth.user} />
        </ProtectedRoute>
      } />
      <Route path="/orderbook-whales" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <OrderBookWhalesDashboard />
        </ProtectedRoute>
      } />
      <Route path="/exchange-keys" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <ExchangeKeysSettings />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/strategy-performance" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <StrategyPerformancePage />
        </ProtectedRoute>
      } />
      <Route path="/power-bot" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <PowerBot auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/stock-trading" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <StockDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/options-trading" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <OptionsTrading auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/options" element={<Navigate to="/options-trading" replace />} />
      <Route path="/stocks" element={<Navigate to="/stock-trading" replace />} />
      <Route path="/signals" element={<Navigate to="/ai-signals" replace />} />
      <Route path="/futures" element={<Navigate to="/trading" replace />} />
      <Route path="/risk" element={<Navigate to="/risk-disclosure" replace />} />
      <Route path="/charts" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Charts auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <Referrals auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/multi-account" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <MultiAccount auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/tax-reporting" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <TaxReporting auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/broker-guide" element={<BrokerGuide />} />
      <Route path="/legal" element={<LegalHub />} />
      <Route path="/legal-documents" element={<LegalDocuments />} />
      <Route path="/legal/agent-agreement" element={<AgentAgreement />} />
      <Route path="/legal/white-label" element={<WhiteLabelAgreement />} />
      <Route path="/legal/developer-msa" element={<DeveloperMSA />} />
      <Route path="/legal/governance" element={<GovernanceCharter />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/public-pricing" element={<PublicPricingPage />} />
      <Route path="/white-label" element={<WhiteLabelPage />} />
      <Route path="/white-label/demo" element={<WhiteLabelDemo />} />
      <Route path="/platform-demo" element={<PlatformDemo />} />
      <Route path="/power-bot-suite" element={<PowerBotSuite auth={auth} />} />
      <Route path="/forex" element={<ForexTrading />} />
      <Route path="/advanced-ai" element={<AdvancedAIEngine auth={auth} />} />
      <Route path="/admin/accuracy" element={<AccuracyDashboard auth={auth} />} />
      <Route path="/admin/deployment" element={<DeploymentChecklist auth={auth} />} />
      <Route path="/task-monitor" element={<TaskMonitor auth={auth} />} />
      <Route path="/notifications" element={<Notifications auth={auth} />} />
      <Route path="/signal-bridge" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SignalBridgeDashboard />
        </ProtectedRoute>
      } />
      <Route path="/trade-activity" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <TradeActivityFeed />
        </ProtectedRoute>
      } />
      <Route path="/backtest" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <BacktestEngine />
        </ProtectedRoute>
      } />
      <Route path="/strategy-optimizer" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <StrategyOptimizer />
        </ProtectedRoute>
      } />
      <Route path="/swing-activity" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <SwingActivityLog />
        </ProtectedRoute>
      } />
      <Route path="/trade-quality" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <TradeQualityDashboard />
        </ProtectedRoute>
      } />
      <Route path="/open-positions" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <OpenPositionsPage />
        </ProtectedRoute>
      } />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/risk-disclosure" element={<RiskDisclosure />} />
      <Route path="/payment-terms" element={<PaymentTerms />} />
      <Route path="/subscribe" element={<Subscribe auth={auth} />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess auth={auth} />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

const PWAInstallPrompt = lazy(() => import('@/components/PWAInstallPrompt'));
const UpdateBanner = lazy(() => import('@/components/UpdateBanner'));
const OfflineIndicator = lazy(() => import('@/components/OfflineIndicator'));

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <SidebarProvider>
          <PushNotificationProvider>
            <div className="App min-h-screen bg-background text-foreground">
              <BrowserRouter>
                <Suspense fallback={null}>
                  <UpdateBanner />
                </Suspense>
                <AppRouter />
                <Suspense fallback={null}>
                  <PWAInstallPrompt />
                  <OfflineIndicator />
                </Suspense>
              </BrowserRouter>
              <Toaster position="top-right" richColors />
            </div>
          </PushNotificationProvider>
        </SidebarProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
// v2.5.0
